import { UploadOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Form,
  Image,
  Input,
  InputNumber,
  Select,
  Spin,
  Upload,
} from "antd";
import { RcFile, UploadProps } from "antd/es/upload";
import { Copy, Package, Plus, X } from "lucide-react";
import { FC, useEffect, useMemo, useState } from "react";
import { InfiniteScrollSelect } from "~/components/common";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { useNotification } from "~/hooks";
import { useGetClients } from "~/services/client/client.queries";
import { useGetLocationsInfinite } from "~/services/common";
import {
  useCreateInventory,
  useEditInventory,
  useGetInventories,
  useGetInventory,
} from "~/services/inventory";
import { useCreateImages } from "~/services/image";
import {
  useGetPresignedUrls,
  useUploadFileToPresignedUrl,
} from "~/services/upload";
import { TImage } from "~/types";
import { capitalize, getImageUrl } from "~/utils";
import {
  ClientStatus,
  ContainerType,
  InventoryStatusCode,
  InventoryType,
  PackingTypes,
} from "~/utils/constants";
import { useInventoryStore } from "../inventory.store";
import { TPresignedUrl } from "~/services/upload/upload.types";
import { useMutation, useQueryClient } from "@tanstack/react-query";

const containerTypes = [
  {
    id: ContainerType.CRATE,
    name: "Crate",
    dimensions: { length: 48, width: 48, height: 48 },
  },
  {
    id: ContainerType.SLATCRATE,
    name: "Slat Crate",
    dimensions: { length: 48, width: 48, height: 48 },
  },
  {
    id: ContainerType.T_Frame,
    name: "T-Frame",
    dimensions: { length: 48, width: 8, height: 48 },
  },
  {
    id: ContainerType.PALLET,
    name: "Pallet",
    dimensions: { length: 48, width: 48, height: 48 },
  },
  {
    id: ContainerType.CBIN,
    name: "C-Bin",
    dimensions: { length: 48, width: 24, height: 28 },
  },
  {
    id: ContainerType.SLIPCASE,
    name: "Slipcase",
    dimensions: { length: 48, width: 48, height: 6 },
  },
  {
    id: ContainerType.BOX,
    name: "Box",
    dimensions: { length: 18, width: 18, height: 24 },
  },
  { id: ContainerType.CUSTOM, name: "Custom" },
];

const containerTypeToPackingType = {
  [ContainerType.CRATE]: PackingTypes.CRATE,
  [ContainerType.SLATCRATE]: PackingTypes.SLATCRATE, // Now maps to specific type
  [ContainerType.T_Frame]: PackingTypes.T_Frame,
  [ContainerType.PALLET]: PackingTypes.PALLET, // Now maps to specific type
  [ContainerType.CBIN]: PackingTypes.CBIN, // Now maps to specific type
  [ContainerType.SLIPCASE]: PackingTypes.SLIPCASE,
  [ContainerType.BOX]: PackingTypes.BOX,
  [ContainerType.CUSTOM]: PackingTypes.CUSTOM,
  [ContainerType.PACKAGE]: PackingTypes.BOX, // Map to closest equivalent
  [ContainerType.PIECE]: PackingTypes.LOOSE, // Map to closest equivalent
};

const mediumTypes = [
  "Oil on Canvas",
  "Acrylic on Canvas",
  "Mixed Media",
  "3D Mixed Media",
  "Sculpture - Bronze",
  "Sculpture - Glass",
  "Photography",
  "Works on Paper",
  "Cast/Forged Metal",
  "2D Flatwork",
  "Other",
];

export interface InventoryFormData {
  itemType: InventoryType;
  clientId: string;
  title: string;
  clientInternalRef?: string;
  length: number;
  width: number;
  height: number;
  weight: number;
  status?: string;
  locationId?: string;
  imageIds?: string[];
  notes?: string;
  duplicates?: number;
  medium?: string;
  value?: number;
  specialHandling?: string[];
  parts?: number;
  containerType?: string;
  parentInventoryId?: string;
  packingType?: string;
  customMedium?: string;
  artistName?: string;
}

type Props = {
  viewMode?: boolean;
};

export const InventoryForm: FC<Props> = ({ viewMode = false }) => {
  const queryClient = useQueryClient();
  const { formState, closeInventoryModal } = useInventoryStore();
  const [form] = Form.useForm<InventoryFormData>();
  const itemType = Form.useWatch("itemType", form);
  const containerType = Form.useWatch("containerType", form);
  const medium = Form.useWatch("medium", form);
  const status = Form.useWatch("status", form); 
  const [showDuplicateInput, setShowDuplicateInput] = useState(false);
  const [showPartInput, setShowPartInput] = useState(false);
  const [imageIdsToDelete, setImageIdsToDelete] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const { data: inventory, isLoading: isLoadingInventory } = useGetInventory(
    formState.inventoryId
  );
  const { data: pieceInventories } = useGetInventories({
    itemType: InventoryType.PIECE,
  });
  const prevImgs = useMemo(
    () =>
      inventory?.images
        ? inventory.images.filter((img) => !imageIdsToDelete.includes(img.id))
        : [],
    [inventory, imageIdsToDelete]
  );
  const infiniteLocations = useGetLocationsInfinite();
  const { data: clients, isLoading: clientsLoading } = useGetClients({
    params: { status: ClientStatus.ACTIVE, sortByName: "ASC", getAll: true },
  });
  const { mutate: createInventory } = useCreateInventory();
  const { mutate: updateInventory } = useEditInventory();
  const getPresignedMutation = useGetPresignedUrls();
  const uploadPresignedMutation = useUploadFileToPresignedUrl();
  const createImagesMutation = useCreateImages();
  const { success, error } = useNotification();

  useEffect(() => {
    if (!inventory) return;
    const mediumValue = mediumTypes.includes(inventory?.medium || "")
      ? inventory.medium
      : "Other";
    form.setFieldsValue({
      ...inventory,
      locationId: inventory.locationId,
      duplicates: 1,
      packingType: inventory.packingType || undefined,
      medium: mediumValue,
      customMedium: mediumValue === "Other" ? inventory.medium : undefined,
      clientInternalRef: inventory.clientInternalRef,
      length: inventory.length,
      width: inventory.width,
      height: inventory.height,
      artistName: inventory.artistName,
    });
  }, [form, inventory]);

 useEffect(() => {
  if (containerType) {
    const selectedContainer = containerTypes.find(
      (item) => item.id === containerType
    );


    if (!formState.inventoryId || form.isFieldsTouched(["containerType"])) {
      // Map containerType to valid PackingType for both PACKAGE and PIECE items
      const mappedPackingType = containerTypeToPackingType[containerType as ContainerType];

      if (itemType === InventoryType.PACKAGE && selectedContainer && selectedContainer.dimensions) {
        const currentLength = form.getFieldValue("length");
        const currentWidth = form.getFieldValue("width");
        const currentHeight = form.getFieldValue("height");

        if (!currentLength && !currentWidth && !currentHeight) {
          form.setFieldsValue({
            length: selectedContainer.dimensions.length,
            width: selectedContainer.dimensions.width,
            height: selectedContainer.dimensions.height,
            packingType: mappedPackingType, // Use mapped value
          });
        } else {
          form.setFieldsValue({ packingType: mappedPackingType }); // Use mapped value
        }
      } else {
        // For PIECE items or PACKAGE items without dimensions, just set packingType
        form.setFieldsValue({ packingType: mappedPackingType }); // Use mapped value
      }
    }
  }
}, [containerType, form, itemType, formState.inventoryId]);


  const clearForm = () => {
    form.resetFields();
    setShowDuplicateInput(false);
    setShowPartInput(false);
    setImageIdsToDelete([]);
  };

  const onCreateInventory = async (values: any) => {
    if (viewMode) return;
    setIsLoading(true);
    const finalValues = {
      ...values,
      medium:
        values.medium === "Other" && values.customMedium
          ? values.customMedium
          : values.medium,
    };
    delete finalValues.customMedium;

    let uploadedImageIds: string[] = [];
    if (values.files && values.files.length > 0) {
      try {
        const fileParams = values.files.map((file: RcFile) => ({
          fileName: file.name,
          mimeType: file.type,
        }));

        const presignedUrls = await getPresignedMutation.mutateAsync(
          fileParams
        );

        const uploadedUrls: TPresignedUrl[] = [];
        for (const [index, urlData] of presignedUrls.entries()) {
          const file = values.files[index];
          if (file) {
            await uploadPresignedMutation.mutateAsync({
              presignedUrl: urlData.presignedUrl,
              file,
            });
            uploadedUrls.push(urlData);
          }
        }

        if (uploadedUrls.length > 0) {
          const images = uploadedUrls.map((presigned, index) => {
            const file = values.files[index];
            return {
              url: presigned.url,
              name: presigned.fileName,
              size: file.size?.toString(),
            };
          });

          const createdImages = await createImagesMutation.mutateAsync({
            images,
          });
          if (Array.isArray(createdImages)) {
            uploadedImageIds = createdImages.map((img: any) => img.id);
          } else if (
            createdImages &&
            typeof createdImages === "object" &&
            "id" in createdImages
          ) {
            uploadedImageIds = [createdImages.id];
          }
        }
      } catch (uploadError) {
        console.error("Error uploading images:", uploadError);
        error({
          message: "Error uploading images",
          description: "There was an error uploading the images.",
        });
        setIsLoading(false);
        return;
      }
    }

    const payload = {
      ...finalValues,
      imageIds: uploadedImageIds,
    };
    delete payload.files;

  

    if (formState.inventoryId) {
      if (imageIdsToDelete && imageIdsToDelete.length > 0) {
        payload.imageIdsToDelete = imageIdsToDelete;
      }

      updateInventory(
        { id: formState.inventoryId, ...payload },
        {
          onSuccess: () => {
            form.resetFields();
            success({
              message: "Inventory item updated successfully!",
              description: "The inventory item has been updated.",
            });
            if (formState.inventoryId) {
              queryClient.invalidateQueries({
                queryKey: ["inventory", formState.inventoryId],
              });
            }
            closeInventoryModal();
          },
          onError: (err) => {
            console.error("Error updating inventory:", err);
            error({
              message: "Error updating inventory item",
              description: "There was an error updating the inventory item.",
            });
          },
          onSettled: () => setIsLoading(false),
        }
      );
    } else {
      createInventory(payload, {
        onSuccess: () => {
          form.resetFields();
          success({
            message: "Inventory item created successfully!",
            description: "The inventory item has been added.",
          });
          closeInventoryModal();
        },
        onError: (err) => {
          console.error("Error creating inventory:", err);
          error({
            message: "Error creating inventory item",
            description: "There was an error creating the inventory item.",
          });
        },
        onSettled: () => setIsLoading(false),
      });
    }
  };

  const mutation = useMutation({ mutationFn: onCreateInventory });

  return (
    <Spin spinning={isLoadingInventory}>
      <Form
        layout="vertical"
        size="large"
        requiredMark={CustomizeRequiredMark}
        form={form}
        onFinish={(values) => mutation.mutate(values)}
        disabled={viewMode}
      >
        <div className="grid grid-cols-2 gap-x-6">
          {formState.inventoryId && (
            <>
              <Form.Item noStyle name="prevImgs" />
              <Form.Item noStyle name="imageIdsToDelete" />
            </>
          )}

          <Form.Item
            label="Item Type"
            name="itemType"
            rules={[{ required: true, message: "Please select an item type" }]}
          >
            <Select
              placeholder="Select item type"
              options={Object.entries(InventoryType).map(([label, value]) => ({
                label: capitalize(label),
                value,
              }))}
            />
          </Form.Item>

          <Form.Item
            label="Client"
            name="clientId"
            rules={[{ required: true, message: "Please select a client" }]}
          >
            <Select
              showSearch
              placeholder="Select a client"
              optionFilterProp="children"
              loading={clientsLoading}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={clients?.items.map((client) => ({
                value: client.id,
                label:
                  client.companyName ||
                  [client.firstName, client.lastName]
                    .filter(Boolean)
                    .join(" ")
                    .trim() ||
                  "Unnamed Client",
              }))}
            />
          </Form.Item>

          {itemType === InventoryType.PART && (
            <Form.Item
              label="Parent Piece"
              name="parentInventoryId"
              rules={[
                { required: true, message: "Please select a parent piece" },
              ]}
            >
              <Select
                placeholder="Select a parent piece"
                showSearch
                optionFilterProp="label"
                options={pieceInventories?.items.map((inv) => ({
                  label: `${inv.title} (${inv.inventoryRef})`,
                  value: inv.id,
                }))}
              />
            </Form.Item>
          )}

          {itemType === InventoryType.PACKAGE && (
            <Form.Item
              label="Container Type"
              name="containerType"
              rules={[
                { required: true, message: "Please select a container type" },
              ]}
            >
              <Select
                options={containerTypes.map((type) => ({
                  label: type.name,
                  value: type.id,
                }))}
                placeholder="Select a container type"
              />
            </Form.Item>
          )}

          {itemType === InventoryType.PIECE && (
            <Form.Item
              label="Medium"
              name="medium"
              rules={[{ required: true, message: "Please select a medium" }]}
            >
              <Select
                options={mediumTypes.map((type) => ({
                  label: type,
                  value: type,
                }))}
                placeholder="Select a medium"
              />
            </Form.Item>
          )}

          {itemType === InventoryType.PIECE && medium === "Other" && (
            <Form.Item
              label="Custom Medium"
              name="customMedium"
              rules={[
                { required: true, message: "Please enter a custom medium" },
              ]}
            >
              <Input placeholder="Enter custom medium type" />
            </Form.Item>
          )}

          <div className="col-span-2">
            <Form.Item
              label="Title/Description"
              name="title"
              rules={[
                {
                  required: true,
                  message: "Please enter a title or description",
                },
              ]}
            >
              <Input placeholder="Enter title or description" />
            </Form.Item>
          </div>

          <div className="col-span-2">
            <Form.Item label="Artist" name="artistName">
              <Input placeholder="Enter artist name (optional)" />
            </Form.Item>
          </div>

          <div className="col-span-2">
            <Form.Item label="Client Reference #" name="clientInternalRef">
              <Input placeholder="Enter client reference number" />
            </Form.Item>
          </div>

          <div className="col-span-2">
            <div className="grid grid-cols-3 gap-4">
              <Form.Item
                label="Length (inches)"
                name="length"
                rules={[{ required: true, message: "Please enter length" }]}
              >
                <InputNumber
                  className="w-full"
                  min="0"
                  step="0.25"
                  placeholder="0.00"
                />
              </Form.Item>
              <Form.Item
                label="Depth (inches)"
                name="width"
                rules={[{ required: true, message: "Please enter width" }]}
              >
                <InputNumber
                  className="w-full"
                  min="0"
                  step="0.25"
                  placeholder="0.00"
                />
              </Form.Item>
              <Form.Item
                label="Height (inches)"
                name="height"
                rules={[{ required: true, message: "Please enter height" }]}
              >
                <InputNumber
                  className="w-full"
                  min="0"
                  step="0.25"
                  placeholder="0.00"
                />
              </Form.Item>
            </div>
          </div>

          <Form.Item
            label="Weight (lbs)"
            name="weight"
            rules={[{ required: true, message: "Please enter weight" }]}
          >
            <InputNumber
              className="w-full"
              min="0"
              step="0.25"
              placeholder="0.00"
            />
          </Form.Item>

          {itemType === InventoryType.PIECE && (
            <Form.Item label="Parts">
              <div className="flex space-x-2">
                <button
                  type="button"
                  className="flex items-center px-3 py-2 border rounded-lg hover:bg-gray-50"
                  onClick={() => setShowPartInput(!showPartInput)}
                >
                  <Plus size={16} className="mr-1" />
                  <span>Add Parts</span>
                </button>
                {showPartInput && (
                  <div className="flex items-center space-x-2">
                    <Form.Item name="parts" noStyle>
                      <InputNumber type="number" min="1" />
                    </Form.Item>
                    <span>parts</span>
                  </div>
                )}
              </div>
            </Form.Item>
          )}

          <Form.Item label="Duplicate">
            <div className="flex space-x-2">
              <button
                type="button"
                className="flex items-center px-3 py-2 border rounded-lg hover:bg-gray-50"
                onClick={() => setShowDuplicateInput(!showDuplicateInput)}
              >
                <Copy size={16} className="mr-1" />
                <span>Duplicate</span>
              </button>
              {showDuplicateInput && (
                <div className="flex items-center space-x-2">
                  <Form.Item name="duplicates" noStyle>
                    <InputNumber type="number" min="1" />
                  </Form.Item>
                  <span>copies</span>
                </div>
              )}
            </div>
          </Form.Item>

     <div className="col-span-2 grid grid-cols-3 gap-4">
  <Form.Item label="Status" name="status">
    <Select
      options={Object.keys(InventoryStatusCode).map((status) => ({
        label: capitalize(status),
        value: status,
      }))}
      placeholder="Select a status"
    />
  </Form.Item>

  <Form.Item
    label="Location"
    name="locationId"
    rules={[
      {
        required: status === InventoryStatusCode.AVAILABLE_ON_HAND,
        message: "Please select a location"
      }
    ]}
  >
    <InfiniteScrollSelect
      getOptionLabel={(item) => item.name}
      getOptionValue={(item) => item.id}
      queryResult={infiniteLocations}
      placeholder="Select a location"
      allOptions={{ value: "", label: "None (Clear Location)" }}
      allowClear
    />
  </Form.Item>
</div>

          {itemType === InventoryType.PIECE && (
            <Form.Item
              label="Value ($)"
              name="value"
              rules={[{ required: true, message: "Please enter a value" }]}
            >
              <InputNumber
                className="w-full"
                min="0"
                step="0.01"
                placeholder="0.00"
              />
            </Form.Item>
          )}

          {itemType === InventoryType.PIECE && (
            <Form.Item label="Special Handling" name="specialHandling">
              <Checkbox.Group>
                {[
                  { label: "High Value", value: "highValue" },
                  { label: "Ride Flat", value: "rideFlat" },
                  { label: "Height Restriction", value: "heightRestriction" },
                ].map((item) => (
                  <Checkbox key={item.value} value={item.value}>
                    {item.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            </Form.Item>
          )}

          {itemType === InventoryType.PIECE && (
            <Form.Item
              label="Packing Type"
              name="packingType"
              rules={[
                { required: true, message: "Please select a packing type" },
              ]}
            >
              <Select
                options={Object.values(PackingTypes).map((type) => ({
                  label: capitalize(type),
                  value: type,
                }))}
                placeholder="Select a packing type"
              />
            </Form.Item>
          )}

          <div className="col-span-2 mt-2">
            <Form.Item label="Photos/Attachments" name="files">
              <UploadButton
                prevImgs={prevImgs}
                onDeleteImg={setImageIdsToDelete}
                disabled={viewMode}
              />
            </Form.Item>
          </div>

          <div className="col-span-2 mt-2">
            <Form.Item label="Notes" name="notes">
              <Input.TextArea
                rows={4}
                placeholder="Enter any additional notes or information..."
                className="border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </Form.Item>
          </div>
        </div>

        {!viewMode && (
          <div className="p-4 border-t bg-gray-50 flex justify-end space-x-3">
            <Button
              htmlType="button"
              className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-100"
              onClick={clearForm}
            >
              Clear
            </Button>
            <Button
              loading={isLoading || mutation.isPending}
              htmlType="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
            >
              <Package size={16} className="mr-2" />
              Save Item
            </Button>
          </div>
        )}
      </Form>
    </Spin>
  );
};

const UploadButton = ({
  value,
  onChange,
  prevImgs,
  onDeleteImg,
  disabled,
}: {
  value?: RcFile[];
  onChange?: (files: RcFile[]) => void;
  prevImgs: TImage[];
  onDeleteImg: React.Dispatch<React.SetStateAction<string[]>>;
  disabled?: boolean;
}) => {
  const [previewImages, setPreviewImages] = useState<
    { url: string; file: File }[]
  >([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (value && value.length > 0) {
      const newPreviews = value.map((file) => ({
        url: URL.createObjectURL(file),
        file,
      }));
      setPreviewImages(newPreviews);
      return () => {
        newPreviews.forEach((preview) => URL.revokeObjectURL(preview.url));
      };
    }
  }, [value]);

  const beforeUpload: UploadProps["beforeUpload"] = (file) => {
    setUploading(true);
    try {
      const previewUrl = URL.createObjectURL(file);
      setPreviewImages((prev) => [...prev, { url: previewUrl, file }]);
      onChange?.(value ? [...value, file] : [file]);
    } catch (error) {
      console.error("Error creating file preview:", error);
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleRemoveFile = (index: number) => {
    if (!value) return;
    const urlToRevoke = previewImages[index].url;
    const newFilesList = value.filter((_, i) => i !== index);
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
    onChange?.(newFilesList);
    URL.revokeObjectURL(urlToRevoke);
  };

  return (
    <>
      <Upload
        accept=".jpg,.jpeg,.png,.gif"
        beforeUpload={beforeUpload}
        showUploadList={false}
        multiple={false}
        disabled={uploading || disabled}
      >
        <Button
          htmlType="button"
          icon={<UploadOutlined />}
          loading={uploading}
          disabled={uploading || disabled}
        >
          {uploading ? "Uploading..." : "Click to Upload"}
        </Button>
      </Upload>

      <div className="mt-3 grid grid-cols-4 gap-4">
        {prevImgs.map((img, index) => (
          <div
            key={`existing-${img.id}`}
            className="relative border rounded-lg p-1"
          >
            <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
              <Image
                src={getImageUrl(img.url)}
                className="object-contain max-w-full max-h-full"
                alt={img.name || `Image ${index + 1}`}
                rootClassName="w-full h-full"
              />
            </div>
            {!disabled && (
              <button
                type="button"
                className="absolute top-1 right-1 bg-white rounded-full p-1 shadow-sm hover:bg-gray-100"
                onClick={() => onDeleteImg((prev) => [...prev, img.id])}
              >
                <X size={14} />
              </button>
            )}
            <div className="text-xs text-gray-500 truncate mt-1 px-1">
              {img.name || "Image " + (index + 1)}
            </div>
          </div>
        ))}

        {previewImages.map((preview, index) => (
          <div key={`new-${index}`} className="relative border rounded-lg p-1">
            <div className="w-full h-32 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
              <Image
                src={preview.url}
                className="object-contain max-w-full max-h-full"
                alt={preview.file.name}
                rootClassName="w-full h-full"
              />
            </div>
            {!disabled && (
              <button
                type="button"
                className="absolute top-1 right-1 bg-white rounded-full p-1 shadow-sm hover:bg-gray-100"
                onClick={() => handleRemoveFile(index)}
              >
                <X size={14} />
              </button>
            )}
            <div className="text-xs text-gray-500 truncate mt-1 px-1">
              {preview.file.name}
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
